import { ReactNode } from 'react';
import { RelatedInventoryRiskItem, RiskCounts, RiskTier } from '../../../Api/Projects/projectsApi';
import { RiskBanner } from './RiskBanner';
import { RiskWidgetCard } from './RiskWidgetCard';
import { RISK_TIER_DEFAULT_LABELS, RISK_TIER_SOLID_COLORS } from './riskTierStyles';

const INSTALLED_CHIP_COLOR = '#6B7280';

// Tile order mirrors the design: highest risk first, Installed last.
const TIER_ORDER: RiskTier[] = ['red', 'yellow', 'green'];

export const riskCountTotal = (counts: RiskCounts): number =>
  counts.green + counts.yellow + counts.red + counts.unscored + counts.installed;

// Resolve each tier's display label from the account-configured labels carried on the scored items,
// defaulting to the standard labels for tiers with no items.
export const deriveTierLabels = (items?: RelatedInventoryRiskItem[]): Record<RiskTier, string> => {
  const labels = { ...RISK_TIER_DEFAULT_LABELS };
  items?.forEach((item) => {
    if (item.riskTier && item.riskLabel) labels[item.riskTier] = item.riskLabel;
  });
  return labels;
};

const percent = (count: number, total: number): number => (total === 0 ? 0 : Math.round((count / total) * 100));

const Tile = ({
  count,
  total,
  label,
  color,
  testId
}: {
  count: number;
  total: number;
  label: string;
  color: string;
  testId: string;
}) => (
  <div
    className="flex items-center gap-3 flex-1"
    data-testid={`risk-tile-${testId}`}>
    <div
      style={{ backgroundColor: color }}
      className="rounded-md min-w-10 h-10 px-2 flex items-center justify-center text-lg font-bold text-white">
      {count}
    </div>
    <div className="leading-tight">
      <div className="text-sm text-white font-medium">{label}</div>
      <div className="text-xs text-grey-4">{percent(count, total)}% of total</div>
    </div>
  </div>
);

export interface RiskSummaryWidgetProps {
  riskCounts: RiskCounts;
  relatedInventory?: RelatedInventoryRiskItem[];
  // Rendered in the Status Update slot; when omitted a neutral placeholder is shown (UPDEV-6922 fills this).
  children?: ReactNode;
  className?: string;
}

export const RiskSummaryWidget = ({ riskCounts, relatedInventory, children, className }: RiskSummaryWidgetProps) => {
  const { green, yellow, red, installed } = riskCounts;
  const total = riskCountTotal(riskCounts);

  // The widget only appears on projects that have related inventory.
  if (total === 0) return null;

  const labels = deriveTierLabels(relatedInventory);
  const counts: Record<RiskTier, number> = { green, yellow, red };

  return (
    <div
      className={`flex flex-col ${className ?? ''}`}
      data-testid="risk-summary-widget"
      data-cy="risk-summary-widget">
      <RiskWidgetCard
        fill
        banner={
          red > 0 && (
            <RiskBanner
              tier="red"
              testId="risk-summary-banner">
              {red} {red === 1 ? 'Service is' : 'Services are'} {labels.red}
            </RiskBanner>
          )
        }>
        <div className="flex items-center gap-4">
          {TIER_ORDER.map((tier) => (
            <Tile
              key={tier}
              testId={tier}
              count={counts[tier]}
              total={total}
              label={labels[tier]}
              color={RISK_TIER_SOLID_COLORS[tier]}
            />
          ))}
          <Tile
            testId="installed"
            count={installed}
            total={total}
            label="Installed"
            color={INSTALLED_CHIP_COLOR}
          />
        </div>

        {children ? (
          <div className="mt-4">{children}</div>
        ) : (
          // Status Update placeholder — shown until the AI executive summary section is provided (UPDEV-6922).
          <div
            className="mt-4 rounded-lg bg-[#1F1F1F] p-4"
            data-testid="status-update-placeholder">
            <div className="text-xs text-grey-4">Status Update</div>
            <div className="text-sm text-grey-3 mt-1">Executive summary coming soon.</div>
          </div>
        )}
      </RiskWidgetCard>
    </div>
  );
};
